import React, { type ReactNode } from 'react';
import { Button as ButtonRAC } from 'react-aria-components';

import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';

import { type LoginBarProps } from './login-bar';

export type LoginBarHeadlessProps = {
  children: ReactNode;
};

const LoginBar = ({ children }: LoginBarHeadlessProps) => {
  const { id, FieldLayout } = useFieldPropsV2<LoginBarProps>();

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => null}
      renderInput={() => <>{children}</>}
      renderDescription={() => null}
    />
  );
};

type LoginBarInnerProps = {
  children: ReactNode;
  className?: string;
  email?: string;
  login?: () => void;
  logout?: () => void;
};

const LoginBarInner = ({
  children,
  className,
  email,
  login,
  logout,
}: LoginBarInnerProps) => {
  const labelElement = applyInternalProps<ButtonInternalProps>(
    findChildOfType(children, LoginBar.Text),
    { email },
  );
  const buttonElement = applyInternalProps<ButtonInternalProps>(
    findChildOfType(children, LoginBar.Button),
    {
      email,
      login,
      logout,
    },
  );

  const labelWithButton = !email
    ? applyInternalProps<TextInternalProps>(labelElement, {
        buttonElement,
        email,
      })
    : labelElement;

  return (
    <div className={className}>
      {email ? (
        <>
          {labelElement}
          {buttonElement}
        </>
      ) : (
        labelWithButton
      )}
    </div>
  );
};

type TextProps = {
  className?: string;
};

type TextInternalProps = {
  buttonElement?: ReactNode;
  email?: string;
};

const LoginBarText = ({ className, ...internal }: TextProps) => {
  const { buttonElement, email } = internal as TextInternalProps;
  const { t } = useTranslation();

  const labelText = email
    ? t('form.login-bar.title.logged-in-state', {
        user: email,
      })
    : t('form.login-bar.title.logged-out-state');

  return (
    <div className={className}>
      {labelText}
      {!email && buttonElement}
    </div>
  );
};

type ButtonProps = {
  className?: string;
};

type ButtonInternalProps = {
  email?: string;
  login?: () => void;
  logout?: () => void;
};

const LoginBarButton = ({ className, ...internal }: ButtonProps) => {
  const { t } = useTranslation();
  const { email, login, logout } = internal as ButtonInternalProps;

  const buttonText = email
    ? t('form.login-bar.action.logout')
    : t('form.login-bar.actions.login');

  const handleClick = () => (email ? logout?.() : login?.());

  return (
    <ButtonRAC className={className} onPress={handleClick}>
      {buttonText}
    </ButtonRAC>
  );
};

LoginBar.LoginBar = LoginBarInner;
LoginBar.Text = LoginBarText;
LoginBar.Button = LoginBarButton;

export { LoginBar };
